import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { ROUTE_DEFINITIONS } from '@/routes';
import type { TableColumn, TableAction, BulkActionItem } from '@/components/ui/tables/Table';
import type { SimpleFilter } from '@/components/ui/filters/FilterDropdown';
import type { DateRangeValue } from '@/components/ui/filters/DateRangeFilter';
import type { SelectorOption } from '@/components/ui/inputs/selector';
import { shiftService } from '@/api/domains/shift';
import type {
  CompanyShiftRequest,
  ShiftRequestCounters,
  ShiftRequestStatus,
  LC_BulkApproveShiftRequestsOut,
  LC_BulkRejectShiftRequestsOut,
  LC_ExportCompanyShiftRequestsIn,
} from '@/api/domains/shift/shift.types';
import { SHIFT_REQUEST_STATUS } from '@/api/domains/shift/shift.types';
import { useAppSelector } from '@/hooks';
import { useUI } from '@/hooks/ui/useUI';
import { usePaginatedList } from '@/hooks/ui/usePaginatedList';
import type { PaginatedFetchParams, PaginatedFetchResult } from '@/hooks/ui/usePaginatedList';
import { usePagePermissions } from '@/hooks/authorization/usePagePermissions';
import { useEmployeeLookupSelector } from '@/hooks/employee/useEmployeeLookupSelector';
import { formatDateToYYYYMMDD } from '@/utils';
import { useShiftChangeRequestsExport } from '@/hooks/attendance/management/useShiftChangeRequestsExport';


// ── Filters (ASIS-5231) ──────────────────────────────────────────────────────
// El backend (listado y contadores) ya acepta estos filtros. La selección de
// colaborador es única y se mapea al parámetro CSV `employeeIds` (un solo id).
export interface ShiftChangeRequestsFilters {
  status?: ShiftRequestStatus;
  dateFrom?: string;
  dateTo?: string;
  employeeId?: string; // undefined / '' = "Todos"
}

// ── Types ──────────────────────────────────────────────────────────────────

export interface UseShiftChangeRequestsAdminResult {
  rows: CompanyShiftRequest[];
  counters: ShiftRequestCounters;
  total: number;
  page: number;
  pageSize: number;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  loading: boolean;
  columns: TableColumn<CompanyShiftRequest>[];
  actions: TableAction<CompanyShiftRequest>[];
  onSearch: (text: string) => void;
  onSortChange: (columnKey: string, direction: 'asc' | 'desc') => void;
  statusFilters: SimpleFilter[];
  filterValues: Record<string, string | string[] | undefined>;
  onChangeFilter: (key: string, value: string | string[]) => void;
  dateRange: DateRangeValue;
  onDateRangeChange: (range: DateRangeValue) => void;
  collaboratorOptions: SelectorOption[];
  collaboratorValue: string;
  onCollaboratorChange: (value: string) => void;
  collaboratorOnLoadMore: () => Promise<void>;
  collaboratorHasNextPage: boolean;
  collaboratorIsLoadingMore: boolean;
  collaboratorOnSearchChange: (searchText: string) => void;
  onExport: () => Promise<void>;
  isExporting: boolean;
  // Bulk operations (ASIS-5235 / ASIS-5236)
  canEdit: boolean;
  bulkActions: BulkActionItem[];
  tableSelectionKey: string;
  onSelectionChange: (selected: CompanyShiftRequest[]) => void;
  isRowSelectable: (row: CompanyShiftRequest) => boolean;
  selectedRequests: CompanyShiftRequest[];
  isBulkApproveModalOpen: boolean;
  onBulkApproveModalClose: () => void;
  onBulkApproveSuccess: (result: LC_BulkApproveShiftRequestsOut) => void;
  isBulkRejectModalOpen: boolean;
  onBulkRejectModalClose: () => void;
  onBulkRejectionSuccess: (result: LC_BulkRejectShiftRequestsOut) => void;
}

const EMPTY_COUNTERS: ShiftRequestCounters = { pending: 0, approved: 0, rejected: 0 };

const STATUS_FILTER_KEY = 'status';

// ASIS-5558: el rango se precarga con el último mes y se recalcula en cada entrada a la
// pantalla (ver `onHydrated`), de modo que la ventana relativa avance con "hoy" en vez de
// quedar congelada en la fecha de la última visita.
const buildDefaultDateRange = (): Pick<ShiftChangeRequestsFilters, 'dateFrom' | 'dateTo'> => {
  const today = new Date();
  const oneMonthAgo = new Date(today);
  oneMonthAgo.setMonth(today.getMonth() - 1);
  return {
    dateFrom: formatDateToYYYYMMDD(oneMonthAgo),
    dateTo: formatDateToYYYYMMDD(today),
  };
};

// ── Column → backend orderBy mapping ─────────────────────────────────────────

const ORDER_BY_MAP: Record<string, string> = {
  id: 'id',
  status: 'status',
  employeeRut: 'employeeRut',
  employeeName: 'employeeName',
  requestDate: 'requestDate',
  dateFrom: 'dateFrom',
  dateTo: 'dateTo',
};

// ── Hook ───────────────────────────────────────────────────────────────────

export function useShiftChangeRequestsAdmin(): UseShiftChangeRequestsAdminResult {
  const { t: translate } = useTranslation();
  const { showApiError, openSidePanel, closeSidePanel } = useUI();
  const navigate = useNavigate();
  const { canView, canEdit, isLoaded: permissionsLoaded } = usePagePermissions();

  // Company is taken from the authenticated user (company-scoped endpoint).
  const companyId = useAppSelector((state) => state.auth.user?.idEmpresa ?? null);

  // ── Permission guard ──────────────────────────────────────────────────────
  useEffect(() => {
    if (permissionsLoaded && !canView) {
      void navigate(ROUTE_DEFINITIONS.DASHBOARD_ASISTENCIA.path);
    }
  }, [permissionsLoaded, canView, navigate]);

  const enabled = permissionsLoaded && canView && companyId !== null;

  // Contadores KPI por estado (ASIS-5237). Endpoint dedicado que aplica los MISMOS
  // filtros vigentes que el listado (estado incluido, ASIS-5231).
  const [counters, setCounters] = useState<ShiftRequestCounters>(EMPTY_COUNTERS);

  // Token para forzar el recálculo de los contadores tras una aprobación,
  // sin cambiar los filtros vigentes.
  const [countersVersion, setCountersVersion] = useState(0);
  const refreshCounters = useCallback(() => setCountersVersion((version) => version + 1), []);

  // ── Fetch del listado: aplica los filtros vigentes ─────────────────────────
  const fetchFn = useCallback(
    async (
      params: PaginatedFetchParams<ShiftChangeRequestsFilters>,
      signal: AbortSignal,
    ): Promise<PaginatedFetchResult<CompanyShiftRequest> | null> => {
      const response = await shiftService.getCompanyShiftRequests(
        {
          status: params.filters.status,
          dateFrom: params.filters.dateFrom,
          dateTo: params.filters.dateTo,
          employeeIds: params.filters.employeeId || undefined,
          searchText: params.searchText,
          orderBy: params.sort?.key,
          orderDirection: params.sort?.direction,
          page: params.page,
          pageSize: params.pageSize,
        },
        signal,
      );

      // Petición cancelada por un fetch más reciente (abort): ignorar en silencio.
      if (response.error?.code === 'ABORTED') return null;

      if (!response.success || !response.data) {
        showApiError(response, translate('attendance:shiftChangeAdmin.messages.loadError'));
        return null;
      }

      return { items: response.data.data, totalRecords: response.data.totalRecords };
    },
    [showApiError, translate],
  );

  const {
    rows,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    loading,
    onSearch,
    onSortChange,
    filters,
    setFilters,
    debouncedSearchText,
    refetch,
  } = usePaginatedList<CompanyShiftRequest, ShiftChangeRequestsFilters>({
    fetchFn,
    initialFilters: buildDefaultDateRange(),
    initialSort: { key: 'requestDate', direction: 'DESC' },
    orderByMap: ORDER_BY_MAP,
    enabled,
    screenCode: ROUTE_DEFINITIONS.GESTION_CAMBIO_TURNO.code,
    onHydrated: (hydrated) => ({ ...hydrated.filters, ...buildDefaultDateRange() }),
  });

  // ── Contadores KPI: misma combinación de filtros vigentes (estado y búsqueda incluidos) ──
  useEffect(() => {
    if (!enabled) {
      setCounters(EMPTY_COUNTERS);
      return;
    }

    const controller = new AbortController();

    void (async () => {
      const response = await shiftService.getCompanyShiftRequestCounters(
        {
          status: filters.status,
          dateFrom: filters.dateFrom,
          dateTo: filters.dateTo,
          employeeIds: filters.employeeId || undefined,
          searchText: debouncedSearchText || undefined,
        },
        controller.signal,
      );

      if (response.error?.code === 'ABORTED') return;

      if (!response.success || !response.data) {
        setCounters(EMPTY_COUNTERS);
        return;
      }

      setCounters(response.data);
    })();

    return () => controller.abort();
  }, [
    enabled,
    filters.status,
    filters.dateFrom,
    filters.dateTo,
    filters.employeeId,
    debouncedSearchText,
    countersVersion,
  ]);

  // ── Filtro Estado ───────────────────────────────────────────────────────────
  const statusFilters: SimpleFilter[] = useMemo(
    () => [
      {
        key: STATUS_FILTER_KEY,
        label: translate('attendance:shiftChangeAdmin.filters.status'),
        placeholder: translate('attendance:shiftChangeAdmin.status.all'),
        options: [
          { value: '', label: translate('attendance:shiftChangeAdmin.status.all') },
          { value: '10', label: translate('attendance:shiftChangeAdmin.status.pending') },
          { value: '20', label: translate('attendance:shiftChangeAdmin.status.approved') },
          { value: '30', label: translate('attendance:shiftChangeAdmin.status.rejected') },
        ],
      },
    ],
    [translate],
  );

  const filterValues = useMemo(
    () => ({ [STATUS_FILTER_KEY]: filters.status != null ? String(filters.status) : '' }),
    [filters.status],
  );

  const onChangeFilter = useCallback(
    (key: string, value: string | string[]) => {
      if (key !== STATUS_FILTER_KEY) return;
      const raw = Array.isArray(value) ? value[0] : value;
      setFilters((previous) => ({
        ...previous,
        status: raw ? (Number(raw) as ShiftRequestStatus) : undefined,
      }));
    },
    [setFilters],
  );

  // ── Filtro Rango de fechas ──────────────────────────────────────────────────
  const dateRange: DateRangeValue = useMemo(
    () => ({ from: filters.dateFrom, to: filters.dateTo }),
    [filters.dateFrom, filters.dateTo],
  );

  const onDateRangeChange = useCallback(
    (range: DateRangeValue) => {
      setFilters((previous) => ({ ...previous, dateFrom: range.from, dateTo: range.to }));
    },
    [setFilters],
  );

  // ── Filtro Colaborador (reutiliza el selector de la pantalla de contratos) ────
  // Se usa el lookup solo como fuente de opciones/paginación/búsqueda; la selección
  // la maneja el estado de filtros, con una opción "Todos" (sin filtro) por defecto.
  const employeeLookup = useEmployeeLookupSelector();

  const collaboratorOptions: SelectorOption[] = useMemo(
    () => [
      { value: '', label: translate('attendance:shiftChangeAdmin.filters.allEmployees') },
      ...employeeLookup.options,
    ],
    [employeeLookup.options, translate],
  );

  const onCollaboratorChange = useCallback(
    (value: string) => {
      setFilters((previous) => ({ ...previous, employeeId: value || undefined }));
    },
    [setFilters],
  );

  // ── Bulk approval (ASIS-5235) / Bulk rejection (ASIS-5236) ──────────────────
  const [selectedRequests, setSelectedRequests] = useState<CompanyShiftRequest[]>([]);
  const [isBulkApproveModalOpen, setIsBulkApproveModalOpen] = useState(false);
  const [isBulkRejectModalOpen, setIsBulkRejectModalOpen] = useState(false);
  const [selectionVersion, setSelectionVersion] = useState(0);

  const tableSelectionKey = useMemo(
    () => `${page}-${pageSize}-${selectionVersion}`,
    [page, pageSize, selectionVersion],
  );

  const onSelectionChange = useCallback((selected: CompanyShiftRequest[]) => {
    setSelectedRequests(selected);
  }, []);

  const isRowSelectable = useCallback(
    (row: CompanyShiftRequest) => row.statusCode === SHIFT_REQUEST_STATUS.PENDING,
    [],
  );

  const onBulkApproveModalClose = useCallback(() => setIsBulkApproveModalOpen(false), []);

  const onBulkApproveSuccess = useCallback((_result: LC_BulkApproveShiftRequestsOut) => {
    setIsBulkApproveModalOpen(false);
    setSelectedRequests([]);
    setSelectionVersion((version) => version + 1);
    refetch();
    refreshCounters();
  }, [refetch, refreshCounters]);

  const onBulkRejectModalClose = useCallback(() => setIsBulkRejectModalOpen(false), []);

  const onBulkRejectionSuccess = useCallback((_result: LC_BulkRejectShiftRequestsOut) => {
    setIsBulkRejectModalOpen(false);
    setSelectedRequests([]);
    setSelectionVersion((version) => version + 1);
    refetch();
    refreshCounters();
  }, [refetch, refreshCounters]);

  const bulkActions: BulkActionItem[] = useMemo(() => canEdit ? [
    {
      label: translate('attendance:shiftChangeAdmin.bulk.approve'),
      icon: 'check',
      onClick: () => setIsBulkApproveModalOpen(true),
    },
    {
      label: translate('attendance:shiftChangeAdmin.bulk.reject'),
      icon: 'close',
      variant: 'danger',
      onClick: () => setIsBulkRejectModalOpen(true),
    },
  ] : [], [canEdit, translate]);

  // ── Export (ASIS-5238): consume los filtros vigentes ─────────────────────────
  const exportFilters: LC_ExportCompanyShiftRequestsIn = useMemo(
    () => ({
      status: filters.status,
      dateFrom: filters.dateFrom,
      dateTo: filters.dateTo,
      employeeIds: filters.employeeId || undefined,
      searchText: debouncedSearchText || undefined,
    }),
    [filters.status, filters.dateFrom, filters.dateTo, filters.employeeId, debouncedSearchText],
  );
  const { onExport, isExporting } = useShiftChangeRequestsExport({ filters: exportFilters });

  // ── Columns (all sortable) ─────────────────────────────────────────────────
  // ASIS-5557: la columna Estado va inmediatamente después de "Fecha hasta" y antes de la
  // columna de acciones ("…"), que `Table` agrega al final.
  const columns: TableColumn<CompanyShiftRequest>[] = useMemo(
    () => [
      { key: 'id', header: translate('attendance:shiftChangeAdmin.table.id'), sortable: true },
      {
        key: 'employeeRut',
        header: translate('attendance:shiftChangeAdmin.table.rut'),
        sortable: true,
        render: (row: CompanyShiftRequest) => row.employeeRut,
      },
      {
        key: 'employeeName',
        header: translate('attendance:shiftChangeAdmin.table.employee'),
        sortable: true,
        render: (row: CompanyShiftRequest) => row.employeeName,
      },
      {
        key: 'requestDate',
        header: translate('attendance:shiftChangeAdmin.table.requestDate'),
        sortable: true,
        render: (row: CompanyShiftRequest) => row.requestDate || '-',
      },
      {
        key: 'dateFrom',
        header: translate('attendance:shiftChangeAdmin.table.dateFrom'),
        sortable: true,
        render: (row: CompanyShiftRequest) => row.dateFrom || '-',
      },
      {
        key: 'dateTo',
        header: translate('attendance:shiftChangeAdmin.table.dateTo'),
        sortable: true,
        render: (row: CompanyShiftRequest) => row.dateTo || '-',
      },
      {
        key: 'status',
        header: translate('attendance:shiftChangeAdmin.table.status'),
        sortable: true,
        render: (row: CompanyShiftRequest) => row.status,
      },
    ],
    [translate],
  );

  // ── Acciones por fila ("…") ──────────────────────────────────────────────────
  // ASIS-5232: abre el panel lateral de detalle reutilizando la fila ya cargada por
  // el listado (sin petición adicional). Aprobar/Rechazar viven dentro del panel.
  const handleViewDetail = useCallback(
    (row: CompanyShiftRequest) => {
      openSidePanel({
        component: 'ShiftChangeRequestDetailPanel',
        title: translate('attendance:shiftChangeAdmin.detail.title'),
        props: {
          request: row,
          // Aprobación exitosa: cierra el panel y refresca listado + KPIs.
          onApproved: () => {
            closeSidePanel();
            refetch();
            refreshCounters();
          },
          // Rechazo exitoso: cierra el panel y refresca listado + KPIs.
          onRejected: () => {
            closeSidePanel();
            refetch();
            refreshCounters();
          },
          // Conflicto de concurrencia: el panel queda en solo lectura; refresca listado + KPIs.
          onConflict: () => {
            refetch();
            refreshCounters();
          },
        },
      });
    },
    [openSidePanel, translate, closeSidePanel, refetch, refreshCounters],
  );

  const actions: TableAction<CompanyShiftRequest>[] = useMemo(
    () => [
      {
        id: 'detail',
        label: translate('common:tableActions.detail'),
        icon: 'eye',
        onClick: handleViewDetail,
      },
    ],
    [translate, handleViewDetail],
  );

  return {
    rows,
    counters,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    loading,
    columns,
    actions,
    onSearch,
    onSortChange,
    statusFilters,
    filterValues,
    onChangeFilter,
    dateRange,
    onDateRangeChange,
    collaboratorOptions,
    collaboratorValue: filters.employeeId ?? '',
    onCollaboratorChange,
    collaboratorOnLoadMore: employeeLookup.onLoadMore,
    collaboratorHasNextPage: employeeLookup.hasNextPage,
    collaboratorIsLoadingMore: employeeLookup.isLoadingMore,
    collaboratorOnSearchChange: employeeLookup.onSearchChange,
    onExport,
    isExporting,
    canEdit,
    bulkActions,
    tableSelectionKey,
    onSelectionChange,
    isRowSelectable,
    selectedRequests,
    isBulkApproveModalOpen,
    onBulkApproveModalClose,
    onBulkApproveSuccess,
    isBulkRejectModalOpen,
    onBulkRejectModalClose,
    onBulkRejectionSuccess,
  };
}

export default useShiftChangeRequestsAdmin;
